import { FC } from 'react';

import { CopyOutlined, PlusCircleOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Skeleton, Typography } from 'antd';
import Search from 'antd/es/input/Search';
import {
  ColumnsType,
  FilterValue,
  SortOrder,
  SorterResult,
} from 'antd/es/table/interface';
import { articleDeleteMessage } from 'common/constants';
import { useQueryParams } from 'common/hook/useQueryParams';
import {
  IArticle,
  IAutocompleteTypeParams,
  TTablePaginationParams,
} from 'common/types';
import { notify, replaceHistoryState } from 'common/utils';
import { actions, Table, text, checkbox } from 'components/Table';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { ArticleModel } from 'store/article-model';

import { ArticlesViewModel } from './articles-view-model';

import styles from './styles.module.scss';

export interface IArticlesProps {
  model: ArticlesViewModel;
}

const Articles: FC<IArticlesProps> = observer(({ model }) => {
  const navigate = useNavigate();

  const params = model._filters.params;

  const searchParams = params.search ? { search: params.search } : '';
  const categoryParams = params.categoryId
    ? { categoryName: params.categoryName, categoryId: params.categoryId }
    : null;

  const create = () => navigate('/articles/new');

  const edit = (id: number) =>
    navigate({
      pathname: `/articles/${id}`,
    });

  const onSearch = (value: string) => {
    model.search = value;

    const search = value ? { search: value } : '';

    replaceHistoryState('/articles', {
      page: '1',
      ...search,
      ...categoryParams,
    });
  };

  const handleChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.value) {
      model.search = '';

      replaceHistoryState('/articles', { page: '1', ...categoryParams });
    }
  };

  const handleSelectCategory = (
    _: string,
    category: IAutocompleteTypeParams,
  ) => {
    model.selectedCategory = category;
    params.categoryId = String(category.id);

    const categoryParams = category.id
      ? { categoryName: category.value, categoryId: String(category.id) }
      : null;

    replaceHistoryState('/articles', {
      page: '1',
      ...searchParams,
      ...categoryParams,
    });
  };

  const handleCopyURL = async (slug: IArticle['slug']) => {
    if (slug) {
      try {
        await navigator.clipboard.writeText(
          `${process.env.REACT_APP_SITE_URL}/article/${slug}`,
        );
        notify('URL was copied to the clipboard', 'success');
      } catch (err) {
        notify('Failed to copy URL', 'error');
      }
    }
  };

  const handleClearCategory = () => {
    model.selectedCategory = null;
    model.searchCategoryValue = '';

    model._filters.page = 1;
    model._filters.params = { page: '1', ...searchParams };

    model.fetchArticles();

    replaceHistoryState('/articles', { page: '1', ...searchParams });
  };

  const handleChangeCategory = (value: string) => {
    model.searchCategoryValue = value;

    if (!value) {
      handleClearCategory();
    }
  };

  const columns: ColumnsType<IArticle> = [
    text({
      width: '10%',
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      loading: model.loading,
    }),
    text({
      width: '10%',
      title: 'Slug',
      dataIndex: 'slug',
      key: 'slug',
      loading: model.loading,
      render: (slug: IArticle['slug']) => {
        return model.loading ? (
          <Skeleton
            active
            paragraph={{ rows: 1 }}
            className={styles.skeleton}
          />
        ) : (
          <div className={styles.slug}>
            <Typography.Text ellipsis={{ tooltip: slug }}>
              {slug}
            </Typography.Text>
            <div className={styles.copyicon}>
              <CopyOutlined onClick={() => handleCopyURL(slug)} />
            </div>
          </div>
        );
      },
    }),
    checkbox({
      width: '7%',
      title: 'Published',
      dataIndex: 'published',
      key: 'published',
      loading: model.loading,
    }),
    text({
      width: '10%',
      title: 'Category',
      dataIndex: 'category.name',
      key: 'category',
      loading: model.loading,
      nested: true,
    }),
    text({
      width: '7%',
      key: 'updatedAt',
      title: 'Last modified',
      dataIndex: 'updatedAt',
      defaultSortOrder: 'descend' as SortOrder | undefined,
      sorter: true,
      loading: model.loading,
    }),
    actions({
      width: '5%',
      onEdit: edit,
      onDelete: model.removeArticle,
      disabled: model.loading,
      deleteMessage: articleDeleteMessage,
    }),
  ];

  const handleChangeTable = (
    pagination: TTablePaginationParams,
    _: Record<string, FilterValue>,
    sorter: SorterResult<ArticleModel>,
  ) => {
    const { current } = pagination;
    model._filters.page = current;

    model._filters.setSorterSortOptions(sorter);

    const search = params.search ? { search: params.search } : '';

    replaceHistoryState('/articles', {
      page: `${current}`,
      ...search,
      ...categoryParams,
    });

    model.fetchArticles();
  };

  console.log('model', model);

  return (
    <div className={styles.articlesPage}>
      <Typography.Title level={1}>Articles</Typography.Title>
      <div className={styles.header}>
        <div className={styles.filters}>
          <Search
            placeholder="Search articles"
            onSearch={onSearch}
            defaultValue={model._filters.search}
            className={styles.search}
            allowClear
            onChange={handleChangeSearch}
          />
          <AutoComplete
            options={model.categoryOptions}
            placeholder="Filter by category"
            onSelect={handleSelectCategory}
            allowClear
            value={model.searchCategoryValue}
            onClear={handleClearCategory}
            onChange={handleChangeCategory}
            className={styles.search}
          />
        </div>
        <Button
          type="primary"
          icon={<PlusCircleOutlined />}
          size="middle"
          onClick={create}
        >
          Add article
        </Button>
      </div>
      <Table
        bordered
        loading={model.loading}
        skeleton={model.skeleton}
        data={model.articles}
        columns={columns}
        pagination={{
          total: model.count,
          disabled: model.loading,
          current: model._filters.defaultCurrent,
        }}
        onChange={handleChangeTable}
      />
    </div>
  );
});

export const ArticlePage = () => {
  const params = useQueryParams();

  const model = new ArticlesViewModel({ ...params, sortBy: 'updatedAt' });

  return <Articles model={model} />;
};
